<template>
  <div>

    <!-- DESKTOP: table -->
    <b-card class="d-none d-md-block">
      <div class="d-flex align-items-center mb-4">
        <b-icon-calendar-fill class="rounded-lg bg-brand-secondary p-3 mr-3" style="width: 40px; height: 40px"
          variant="light" />
        <h4 class="mb-0">{{ $t("Planning") }} · {{ $t("pages.home.7days") }}</h4>
      </div>
      <cleanings-planning-table :bookings="filteredCleanings" :loading="loadingPlanning"
        @toggle-cleaning="onToggleCleaning" />
    </b-card>

    <!-- MOBILE: card list -->
    <b-card id="planning-card" class="d-block d-md-none mb-3">
      <div class="d-flex align-items-center">
        <b-icon-calendar-fill class="rounded-lg bg-brand-secondary p-3 mr-3" style="width: 40px; height: 40px"
          variant="light" />
        <h4 class="mb-0">{{ $t("Planning") }} · {{ $t("pages.home.7days") }}</h4>
      </div>
    </b-card>

    <div class="d-block d-md-none">
      <b-card v-if="loadingPlanning" class="text-center text-primary my-2">
        <b-spinner class="align-middle mr-2" />
        <strong>{{ $t("Carregant") }}...</strong>
      </b-card>
      <b-card v-else-if="!filteredCleanings || !filteredCleanings.length" class="text-center py-2">
        <p class="font-weight-bold mb-25">{{ $t('errors.emptyTable.title') }}</p>
        <p class="text-muted small mb-0">{{ $t('No hi ha serveis de neteja per al període seleccionat') }}</p>
      </b-card>
      <div v-else class="cleanings-planning-list">
        <cleanings-planning-list-item v-for="booking in filteredCleanings" :key="booking.uuid" :booking="booking"
          class="mb-3" @toggle-cleaning="onToggleCleaning" />
      </div>
    </div>

    <b-button v-if="!loadingPlanning" block variant="primary" :to="{ name: 'foravila-cleaning-planning' }" class="mt-4">
      {{ $t("pages.home.allServices") }}
    </b-button>

    <!-- CONFIRM MODAL -->
    <b-modal
      v-model="confirmModalVisible"
      :title="pendingToggle && pendingToggle.cleaningCompleted ? 'Marcar neteja com a pendent' : 'Marcar neteja com a completada'"
      centered
      hide-footer
      @hidden="pendingToggle = null"
    >
      <div v-if="pendingToggle" class="px-25 pb-25">
        <p class="mb-2">
          <span v-if="pendingToggle.cleaningCompleted">
            Vols marcar la neteja de <strong>{{ pendingToggle.accommodation }}</strong>
            ({{ pendingToggle.localizator }}) com a <strong>pendent</strong>?
          </span>
          <span v-else>
            Vols marcar la neteja de <strong>{{ pendingToggle.accommodation }}</strong>
            ({{ pendingToggle.localizator }}) com a <strong>completada</strong>?
          </span>
        </p>
        <b-button
          block
          variant="primary"
          :disabled="updatingCleaning"
          class="mb-75"
          @click="confirmToggleCleaning"
        >
          <b-spinner v-if="updatingCleaning" small class="mr-50" />
          {{ pendingToggle.cleaningCompleted ? 'Marcar com a pendent' : 'Marcar com a completada' }}
        </b-button>
        <b-button
          block
          variant="outline-secondary"
          :disabled="updatingCleaning"
          @click="confirmModalVisible = false"
        >
          Cancel·lar
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import {
  BCard, BButton, BIconCalendarFill, BSpinner, BModal,
} from "bootstrap-vue";
import CleaningsPlanningTable from "@/views/cleaning/components/CleaningsPlanningTable.vue";
import CleaningsPlanningListItem from "@/views/cleaning/components/CleaningsPlanningListItem.vue";
import { notifyError } from "@methods";

export default {
  components: {
    BCard, BButton, BIconCalendarFill, BSpinner, BModal,
    CleaningsPlanningTable, CleaningsPlanningListItem,
  },
  data() {
    return {
      confirmModalVisible: false,
      pendingToggle: null,
      updatingCleaning: false,
    };
  },
  computed: {
    loadingPlanning() {
      return this.$store.getters["bookings/loadingPlanning"];
    },
    cleaningsPlanning() {
      return this.$store.getters["bookings/checkinsPlanning"].filter(
        (booking) => booking.contract?.cleaningProvided
      );
    },
    filteredCleanings() {
      const today = this.$moment().startOf("day");
      const endDate = this.$moment().add(7, "days").startOf("day");

      return this.cleaningsPlanning
        .filter((booking) => {
          const checkin = this.$moment(booking.checkin).startOf("day");
          return checkin.isSameOrAfter(today) && checkin.isBefore(endDate);
        })
        .sort((a, b) => new Date(a.checkin) - new Date(b.checkin))
        .map((booking) => ({
          uuid: booking.uuid || this.$t("Not defined"),
          localizator: booking.localizator || this.$t("Not defined"),
          source: booking.source || null,
          guests: `${booking.adults || 0} - ${booking.children || 0} - ${booking.babies || 0}`,
          accommodation: booking.accommodation?.name || this.$t("Not defined"),
          accommodationUuid: booking.accommodation?.uuid || null,
          accommodationSlug: booking.accommodation?.slug || null,
          client: booking.client?.fullName || this.$t("Not defined"),
          checkin: new Date(booking.checkin),
          checkinTime: booking.details?.accommodationArrivalTime || this.$t("Not defined"),
          checkout: new Date(booking.checkout),
          checkoutTime: booking.details?.accommodationDepartureTime || this.$t("Not defined"),
          babyChairs: booking.details?.babyChairs || null,
          babyChairsText: this.babyChairsText(booking),
          babyCots: booking.details?.babyCots || null,
          babyCotsText: this.babyCotsText(booking),
          bedLinenChange: (booking.bedLinenChanges || []).map((d) => new Date(d)),
          cleaningCompleted: booking.stats?.cleaningCompleted ?? false,
          statsUuid: booking.stats?.uuid || null,
        }));
    },
  },
  created() {
    this.fetchPlanning();
  },
  methods: {
    fetchPlanning() {
      this.$store.dispatch("bookings/fetchPlanning").catch(() => {
        notifyError(
          this.$t("errors.fetchBooking.title"),
          this.$t("errors.fetchBooking.description")
        );
      });
    },
    babyChairsText(booking) {
      if (!booking?.details || booking.details.babyChairs == null) return `? ${this.$t("trones")}`;
      const n = booking.details.babyChairs;
      return n === 0 ? this.$t("No") : `${n} ${this.$t("trones")}`;
    },
    babyCotsText(booking) {
      if (!booking?.details || booking.details.babyCots == null) return `? ${this.$t("cunes")}`;
      const n = booking.details.babyCots;
      return n === 0 ? this.$t("No") : `${n} ${this.$t("cunes")}`;
    },
    onToggleCleaning(booking) {
      this.pendingToggle = booking;
      this.confirmModalVisible = true;
    },
    confirmToggleCleaning() {
      if (!this.pendingToggle) return;
      this.updatingCleaning = true;
      const { statsUuid, cleaningCompleted } = this.pendingToggle;
      this.$store
        .dispatch("bookings/updateBookingStats", { uuid: statsUuid, cleaningCompleted: !cleaningCompleted })
        .then(() => {
          this.confirmModalVisible = false;
        })
        .catch(() => {
          notifyError(
            this.$t("errors.fetchBooking.title"),
            this.$t("errors.fetchBooking.description")
          );
        })
        .finally(() => {
          this.updatingCleaning = false;
        });
    },
  },
};
</script>
